import React from 'react';
import { Button, DatePicker, Input, Radio, Spin, Table } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import type { ColumnsType } from 'antd/es/table';
import { useM302Query } from './hooks/useM302Query';
import type { M302ReportRow } from '../../services/m302Api';
import './M302Form.css';

const columns: ColumnsType<M302ReportRow> = [
  {
    title: '庫位',
    dataIndex: 'fm030_01',
    key: 'fm030_01',
    width: 80,
    sorter: (a, b) => a.fm030_01.localeCompare(b.fm030_01),
  },
  {
    title: '庫位名稱',
    dataIndex: 'loc_name',
    key: 'loc_name',
    width: 120,
    ellipsis: true,
  },
  {
    title: '產品代號',
    dataIndex: 'fm030_02',
    key: 'fm030_02',
    width: 130,
    sorter: (a, b) => a.fm030_02.localeCompare(b.fm030_02),
    render: (v: string) => {
      if (v && v.length === 12) {
        return `${v.substring(0, 6)}-${v.substring(6)}`;
      }
      return v;
    },
  },
  {
    title: '產品名稱',
    dataIndex: 'prod_name',
    key: 'prod_name',
    width: 180,
    ellipsis: true,
  },
  {
    title: '批號',
    dataIndex: 'fm030_03',
    key: 'fm030_03',
    width: 130,
    sorter: (a, b) => a.fm030_03.localeCompare(b.fm030_03),
  },
  {
    title: '單位',
    dataIndex: 'fm030_12',
    key: 'fm030_12',
    width: 60,
  },
  {
    title: '入庫日期',
    dataIndex: 'fm030_13',
    key: 'fm030_13',
    width: 110,
    render: (v: string | null) => v ? dayjs(v).format('YYYY-MM-DD') : '-',
    sorter: (a, b) => (a.fm030_13 || '').localeCompare(b.fm030_13 || ''),
  },
  {
    title: '庫存數量',
    dataIndex: 'fm030_16',
    key: 'fm030_16',
    width: 110,
    align: 'right',
    render: (v: number) => {
      const color = v < 0 ? '#f5222d' : v === 0 ? '#8c8c8c' : '#262626';
      return <span style={{ color, fontWeight: v < 0 ? 600 : 400 }}>{v.toFixed(2)}</span>;
    },
    sorter: (a, b) => a.fm030_16 - b.fm030_16,
  },
  {
    title: '庫存金額',
    dataIndex: 'fm030_17',
    key: 'fm030_17',
    width: 120,
    align: 'right',
    render: (v: number) => v.toFixed(2),
    sorter: (a, b) => a.fm030_17 - b.fm030_17,
  },
  {
    title: '平均單價',
    dataIndex: 'unit_price',
    key: 'unit_price',
    width: 100,
    align: 'right',
    render: (v: number) => v.toFixed(3),
    sorter: (a, b) => a.unit_price - b.unit_price,
  },
];

const M302Form: React.FC = () => {
  const {
    loading, records, grandTotals, total,
    startWarehouse, setStartWarehouse, handleStartWarehouseBlur,
    lastWarehouse, setLastWarehouse,
    startProduct, setStartProduct, handleStartProductBlur,
    lastProduct, setLastProduct,
    startBatch, setStartBatch, handleStartBatchBlur,
    lastBatch, setLastBatch,
    startDate, setStartDate,
    lastDate, setLastDate,
    excludeZero, setExcludeZero,
    handleQuery,
  } = useM302Query();

  return (
    <Spin spinning={loading}>
      <div className="m302-form">
        <h2 style={{ marginBottom: 16 }}>
          庫位庫存量表 -- pwm302f1
          {total > 0 && (
            <span style={{ marginLeft: 12, fontSize: 14, fontWeight: 400, color: '#595959' }}>
              共 {total} 筆
            </span>
          )}
        </h2>

        <div className="filter-section">
          {/* BR-001: Warehouse range */}
          <div className="filter-row">
            <span className="filter-label">庫位代號:</span>
            <Input
              placeholder="[從]"
              value={startWarehouse}
              onChange={(e) => setStartWarehouse(e.target.value)}
              onBlur={handleStartWarehouseBlur}
              maxLength={5}
              style={{ width: 120 }}
            />
            <span className="filter-sep">~</span>
            <Input
              placeholder="[至]"
              value={lastWarehouse}
              onChange={(e) => setLastWarehouse(e.target.value)}
              maxLength={5}
              style={{ width: 120 }}
            />
          </div>

          {/* BR-002: Product range */}
          <div className="filter-row">
            <span className="filter-label">產品代號:</span>
            <Input
              placeholder="[從] XXXXXX-XXXXXX"
              value={startProduct}
              onChange={(e) => setStartProduct(e.target.value)}
              onBlur={handleStartProductBlur}
              maxLength={12}
              style={{ width: 160 }}
            />
            <span className="filter-sep">~</span>
            <Input
              placeholder="[至] XXXXXX-XXXXXX"
              value={lastProduct}
              onChange={(e) => setLastProduct(e.target.value)}
              maxLength={12}
              style={{ width: 160 }}
            />
          </div>

          {/* BR-003: Batch range */}
          <div className="filter-row">
            <span className="filter-label">批號:</span>
            <Input
              placeholder="[從]"
              value={startBatch}
              onChange={(e) => setStartBatch(e.target.value)}
              onBlur={handleStartBatchBlur}
              maxLength={12}
              style={{ width: 160 }}
            />
            <span className="filter-sep">~</span>
            <Input
              placeholder="[至]"
              value={lastBatch}
              onChange={(e) => setLastBatch(e.target.value)}
              maxLength={12}
              style={{ width: 160 }}
            />
          </div>

          {/* BR-004: Date range */}
          <div className="filter-row">
            <span className="filter-label">入庫日期:</span>
            <DatePicker
              placeholder="[從]"
              value={startDate ? dayjs(startDate) : null}
              onChange={(d) => setStartDate(d ? d.format('YYYY-MM-DD') : '')}
              style={{ width: 140 }}
            />
            <span className="filter-sep">~</span>
            <DatePicker
              placeholder="[至]"
              value={lastDate ? dayjs(lastDate) : null}
              onChange={(d) => setLastDate(d ? d.format('YYYY-MM-DD') : '')}
              style={{ width: 140 }}
            />
          </div>

          {/* BR-005: Exclude zero inventory */}
          <div className="filter-row">
            <span className="filter-label">庫存量為零:</span>
            <Radio.Group
              value={excludeZero}
              onChange={(e) => setExcludeZero(e.target.value)}
            >
              <Radio value={false}>全部(含零庫存)</Radio>
              <Radio value={true}>不含零庫存</Radio>
            </Radio.Group>
          </div>
        </div>

        {/* Query button */}
        <div className="action-row">
          <Button
            type="primary"
            icon={<SearchOutlined />}
            onClick={handleQuery}
            loading={loading}
          >
            開始查詢
          </Button>
        </div>

        {/* Grand totals summary */}
        {grandTotals.length > 0 && (
          <div className="summary-section">
            {grandTotals.map((gt) => (
              <div key={gt.fm030_12} className="summary-card">
                <span className="summary-unit">{gt.fm030_12}</span>
                <span className="summary-value">
                  數量: {gt.quantity.toFixed(2)} / 金額: {gt.amount.toFixed(2)}
                </span>
              </div>
            ))}
          </div>
        )}

        {/* Results table */}
        <Table<M302ReportRow>
          columns={columns}
          dataSource={records}
          rowKey={(r) => `${r.fm030_01}-${r.fm030_02}-${r.fm030_03}-${r.fm030_12}`}
          pagination={{
            pageSize: 100,
            showSizeChanger: true,
            showTotal: (t) => `共 ${t} 筆`,
          }}
          size="small"
          scroll={{ x: 1200 }}
          locale={{ emptyText: '請設定篩選條件後按「開始查詢」' }}
        />
      </div>
    </Spin>
  );
};

export default M302Form;
